export type ClientErrorSource = 'render' | 'rejection' | 'window';

export interface TelemetryEvents {
  session_started: Record<string, never>;
  session_ended: { durationMs: number; activeMs: number };
  user_identified: Record<string, never>;
  page_viewed: { path: string; from?: string };
  ui_click: {
    target: string;
    label?: string;
    id?: string;
    kind: string;
    checked?: boolean;
    page?: string;
    modal?: string;
  };
  modal_opened: { modal: string; orderPid?: string };
  modal_closed: { modal: string; orderPid?: string };
  order_opened: {
    pid: string;
    status?: string;
    kind: string;
    via: 'row' | 'url';
  };
  order_status_moved: {
    pid: string;
    from: string;
    to: string;
    withLots: boolean;
  };
  order_cancelled: { pid: string; status?: string };
  lots_saved: {
    pid: string;
    kitLots: number;
    boxLots: number;
    unsavedCount: number;
  };
  order_pdf_downloaded: { pid: string };
  // `value` only for dropdown filters; typed text is a customer's name, email or phone.
  filter_changed: {
    page: string;
    field: string;
    value?: string;
    cleared: boolean;
  };
  thunderbird_order_created: Record<string, never>;
  user_created: Record<string, never>;
  user_updated: Record<string, never>;
  user_deleted: Record<string, never>;
  password_changed: { byAdmin: boolean };
  auto_refresh_toggled: { enabled: boolean };
  auto_refresh_fired: { page: string };
  logout_clicked: Record<string, never>;
  api_error: {
    endpoint: string;
    method: string;
    status: number;
    durationMs: number;
    kind: 'http' | 'network';
  };
  request_failed: {
    kind: 'query' | 'mutation';
    key: string;
    message: string;
    isSessionError: boolean;
  };
  client_error: {
    message: string;
    source: ClientErrorSource;
    componentStack?: string;
  };
  // Ask AI. Never the question or the answer: only sizes, timings and outcomes.
  ai_message_sent: {
    messageLength: number;
    isRetry: boolean;
    hasConversation: boolean;
  };
  ai_response_received: {
    latencyMs: number;
    toolCalls: number;
    filesCreated: number;
    hadError: boolean;
    aborted: boolean;
  };
  ai_stream_error: { kind: string; status?: number };
  ai_file_downloaded: { fileId: number; extension: string; ok: boolean };
}

export type TelemetryEventName = keyof TelemetryEvents;

/** Numeric properties routed to `customMeasurements`; every other value becomes a string dimension. */
export const MEASUREMENT_KEYS = new Set([
  'durationMs',
  'activeMs',
  'kitLots',
  'boxLots',
  'unsavedCount',
  'status',
  'latencyMs',
  'toolCalls',
  'messageLength',
  'filesCreated',
]);
